import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUpdateConcept } from "@/hooks/useConcepts";
import { type SampleGrade, type SharpenMark, useSampleGrade, useSharpenQuestion } from "@/hooks/useSetup";
import { plainText } from "@/lib/conceptGuidance";
import { trackEvent } from "@/lib/engagement";
import { BAND_BADGE, band, countWord, type Mark, shortTitle, verdictFor } from "@/lib/setup/grades";
import { bandsCover, lanePick, questionHash } from "@/lib/setup/lanes";
import { type Direction, mergeNetworks, paperKey, pickSample, previewStats, type PreviewPaper } from "@/lib/setup/preview";
import { cn } from "@/lib/utils";
import { blameSeed, laneOffTopic, OFF_TOPIC_MIN, offTopicKeys, type RefindBlame } from "@/lib/setup/refind";
import { netKey, networksFor, useSetupStore } from "@/stores/setup-store";
import type { Concept } from "@/types/concept";
import { GradeStrip } from "./GradeStrip";
import { httpError } from "./http";
import { LoadingLine } from "./LoadingLine";
import { OffTopicBox } from "./OffTopicBox";
import { type Accepted, SharpenBox } from "./SharpenBox";

const DIRECTION_LABEL: Record<Direction, string> = {
  citations: "cites your seeds",
  references: "your seeds cite it",
  recommendations: "similar to your seeds",
  cocited: "cited alongside your seeds",
};

// The outline variant sets dark:border-input / dark:bg-input/30, which win over
// un-prefixed classes in dark mode (tailwind-merge keeps both) — repeat them as dark:.
const MARK_ON: Record<Mark, string> = {
  right: "border-green-500 bg-green-500/15 text-green-600 hover:bg-green-500/20 dark:border-green-500 dark:bg-green-500/15 dark:text-green-400 dark:hover:bg-green-500/20",
  wrong: "border-destructive bg-destructive/15 text-destructive hover:bg-destructive/20 dark:border-destructive dark:bg-destructive/15 dark:hover:bg-destructive/20",
};

const toSamplePaper = (x: PreviewPaper) => ({
  key: paperKey(x.paper), title: x.paper.title, authors: x.paper.authors ?? [], year: x.paper.year ?? null,
  abstract: x.paper.abstract ?? null, journal: x.paper.journal ?? "", citations: x.paper.citations ?? 0,
  doi: x.paper.doi ?? null,
});

export function SampleStep({ concept, onRefind, onStart, autoStart }: {
  concept: Concept;
  /** ② in re-find mode; blame is null when the sample wasn't judged off-topic. */
  onRefind: (blame: RefindBlame | null) => void;
  /** Start → the page moves to ④, which runs the build. */
  onStart: () => void;
  /** ② was skipped: no sample grading, start straight away. */
  autoStart?: boolean;
}) {
  const checked = useSetupStore((s) => s.checked);
  const networks = useSetupStore((s) => s.networks);
  const resolved = useSetupStore((s) => s.resolved);
  const merged = useMemo(
    () => mergeNetworks(networksFor({ networks, resolved }, checked, concept.scope?.recency)),
    [networks, resolved, checked, concept.scope],
  );
  // ②'s quick-check bands, read once: results still landing must not reshuffle
  // a sample that is already being graded. Used only when every ticked seed is
  // loaded and checked (lanes.bandsCover).
  const [bands] = useState(() => {
    const st = useSetupStore.getState();
    const nets = networksFor(st, st.checked, concept.scope?.recency);
    const b = st.triage[questionHash(concept.abstract ?? "")];
    return nets.length === st.checked.length && bandsCover(nets.map((n) => lanePick(n, concept.scope)), b) ? b : undefined;
  });
  const sample = useMemo(() => pickSample(merged, concept.scope, 8, bands), [merged, concept.scope, bands]);
  const toGrade = useMemo(() => previewStats(merged, concept.scope).inRange, [merged, concept.scope]);
  const facetId = concept.facet_list?.[0]?.id;
  const grade = useSampleGrade(concept.id, facetId);
  const sharpen = useSharpenQuestion(concept.id, facetId);
  const update = useUpdateConcept(concept.id);
  const [grades, setGrades] = useState<Map<string, SampleGrade>>(new Map());
  const [failed, setFailed] = useState<string | null>(null);
  const [marks, setMarks] = useState<Map<string, Mark>>(new Map());
  const [boxOpen, setBoxOpen] = useState<"marks" | "edit" | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [regradeError, setRegradeError] = useState<string | null>(null);
  // Own pending flag, not grade.isPending: StrictMode's mount→unmount→remount detaches the
  // MutationObserver from the in-flight mutation, leaving isPending stuck true.
  const [grading, setGrading] = useState(false);
  const ran = useRef(false);
  // A mounted ref, not an effect-cleanup flag: StrictMode re-runs the effect and resets it.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback(async () => {
    setFailed(null);
    setGrading(true);
    try {
      const res = await grade.mutateAsync(sample.map(toSamplePaper));
      if (mounted.current) setGrades(new Map(res.map((g) => [g.key, g])));
    } catch (err) {
      const { status, detail } = await httpError(err);
      if (mounted.current) setFailed(status === 429 && detail ? detail : "Sample grading didn't work this time. You can skip it.");
    } finally {
      if (mounted.current) setGrading(false);
    }
  }, [grade, sample]);

  useEffect(() => {
    if (!autoStart && !ran.current && sample.length && concept.facet_list?.length) { ran.current = true; void run(); }
  }, [autoStart, sample.length, concept.facet_list, run]);

  // Graded rows by score (desc), then the ungraded in sample order.
  const rows = useMemo(() => {
    const withGrade = sample.map((x) => ({ x, key: paperKey(x.paper), g: grades.get(paperKey(x.paper)) }));
    const graded = withGrade.filter((r) => r.g).sort((a, b) => b.g!.score - a.g!.score);
    return [...graded, ...withGrade.filter((r) => !r.g)];
  }, [sample, grades]);

  const marked: (SharpenMark & { mark: Mark })[] = rows.flatMap(({ x, key, g }) => {
    const m = marks.get(key);
    return g && m
      ? [{ mark: m, title: x.paper.title, abstract: x.paper.abstract ?? null, score: g.score, justification: g.justification, verdict: verdictFor(m, g.score) }]
      : [];
  });
  const strip = ({ title, abstract, score, justification, verdict }: SharpenMark & { mark: Mark }): SharpenMark =>
    ({ title, abstract, score, justification, verdict });
  const wrong = marked.filter((m) => m.mark === "wrong").map(strip);
  const allMarks = marked.map(strip);
  // The box remounts when marks change, so a suggestion never outlives the marks it was built from.
  const marksKey = [...marks].map(([k, m]) => `${k}:${m}`).sort().join("|");

  // Spec 2026-10-31 §1: an off-topic sample with fair grades is a seed problem.
  // With ②'s bands the sample is drawn hit/borderline/miss, so judge the
  // ticked seeds' checked papers instead (refind.laneOffTopic).
  const laneOff = useMemo(() => {
    if (!bands) return null;
    const seeds = checked.flatMap((s) => {
      const id = resolved[s.key];
      const net = id ? networks[netKey(id, concept.scope?.recency)] : undefined;
      return net ? [{ key: s.key, pick: lanePick(net, concept.scope) }] : [];
    });
    return laneOffTopic(seeds, bands);
  }, [bands, checked, resolved, networks, concept.scope]);
  const off = useMemo(() => offTopicKeys(grades, marks), [grades, marks]);
  const showOffTopic = bands ? !!laneOff : !grading && !failed && off.length >= OFF_TOPIC_MIN;
  const offCount = laneOff ? laneOff.off : off.length;
  const offTotal = laneOff ? laneOff.checked : grades.size;
  const blame = useMemo((): RefindBlame | null => {
    if (!showOffTopic) return null;
    const label = (key: string) => {
      const seed = checked.find((s) => s.key === key);
      return seed ? shortTitle(seed.title, 50) : null;
    };
    if (laneOff) {
      const l = laneOff.blame && label(laneOff.blame.key);
      return laneOff.blame && l ? { key: laneOff.blame.key, label: l, offTopic: laneOff.blame.count, total: laneOff.off } : null;
    }
    const seeds = checked.flatMap((s) => {
      const id = resolved[s.key];
      const net = id ? networks[netKey(id, concept.scope?.recency)] : undefined;
      return net ? [{ key: s.key, net }] : [];
    });
    const hit = blameSeed(off, seeds);
    const l = hit && label(hit.key);
    return hit && l ? { key: hit.key, label: l, offTopic: hit.count, total: off.length } : null;
  }, [showOffTopic, laneOff, off, checked, resolved, networks, concept.scope]);
  // Once per grading result (a regrade makes a new map).
  // Lanes basis: once per mount (the bands are frozen).
  const offLogged = useRef<Map<string, SampleGrade> | "lanes" | null>(null);
  useEffect(() => {
    const token = laneOff ? "lanes" : grades;
    if (!showOffTopic || offLogged.current === token) return;
    offLogged.current = token;
    trackEvent("setup_offtopic_shown", {
      concept_id: concept.id, off: offCount, n: offTotal, blamed: !!blame, basis: laneOff ? "lanes" : "sample",
    });
  }, [showOffTopic, laneOff, grades, offCount, offTotal, blame, concept.id]);
  const refind = (from: "box" | "link") => {
    trackEvent("setup_refind_clicked", { concept_id: concept.id, from });
    onRefind(from === "box" ? blame : null);
  };

  function toggle(key: string, mark: Mark, score: number) {
    const next = new Map(marks);
    if (marks.get(key) === mark) next.delete(key);
    else {
      next.set(key, mark);
      trackEvent("sample_marked", { concept_id: concept.id, verdict: verdictFor(mark, score), score, flow: "boards" });
      // An open editor stays put (a remount would drop typed text); Cancel, then mark.
      if (mark === "wrong" && boxOpen !== "edit") setBoxOpen("marks");
    }
    setMarks(next);
    if (![...next.values()].includes("wrong") && boxOpen === "marks") setBoxOpen(null);
  }

  // useUpdateConcept only invalidates: until the refetch lands (or if it fails)
  // the concept prop still holds the old question, so keep the confirmed one.
  const [savedQuestion, setSavedQuestion] = useState<string | null>(null);
  const question = savedQuestion ?? plainText(concept.abstract);

  async function apply(text: string, accepted: Accepted | null): Promise<boolean> {
    setRegradeError(null);
    try {
      await update.mutateAsync({ abstract: text });
      setSavedQuestion(text);
    } catch {
      setRegradeError("Couldn't save the research question. Try again.");
      return false;
    }
    if (accepted) trackEvent("sample_sharpened", { concept_id: concept.id, marked: wrong.length, accepted, flow: "boards" });
    trackEvent("sample_regraded", { count: sample.length, concept_id: concept.id });
    // The old grades answered the old question: drop them before regrading.
    setGrades(new Map());
    setMarks(new Map());
    setBoxOpen(null);
    await run();
    return true;
  }

  function keep(sawSuggestion: boolean) {
    if (sawSuggestion) trackEvent("sample_sharpened", { concept_id: concept.id, marked: wrong.length, accepted: "kept", flow: "boards" });
    setBoxOpen(null);
  }

  // A ref survives StrictMode's simulated remount, so this hands off exactly once.
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoStart && !autoRan.current) { autoRan.current = true; onStart(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once
  }, [autoStart]);

  const gradedItems = rows.filter((r) => r.g).map((r) => ({ key: r.key, title: r.x.paper.title, score: r.g!.score }));
  const marksLocked = grading || update.isPending || sharpen.isPending;

  // ② was skipped: the page switches to ④ straight away, nothing to show here.
  if (autoStart) return null;

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">Do these grades look right?</h1>
        <p className="text-muted-foreground">
          {countWord(sample.length)} papers from your preview, graded with the real prompt. Mark any that look wrong
          and we'll suggest a sharper question. Nothing is saved yet.
        </p>
      </div>

        {sample.length === 0 && <p>No preview papers to grade yet. Go back to seeds, or start now.</p>}
        {grading && <LoadingLine>Grading {sample.length} sample papers…</LoadingLine>}
        {gradedItems.length > 0 && <GradeStrip items={gradedItems} active={active} onActive={setActive} />}

        {rows.length > 0 && (
          <ul className="flex flex-col divide-y divide-border/60 overflow-hidden rounded-lg border bg-card">
            {rows.map(({ x, key, g }) => {
              const m = marks.get(key);
              return (
                <li key={key}
                  onMouseEnter={() => setActive(key)} onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(key)} onBlur={() => setActive(null)}
                  className={cn("flex items-center gap-4 px-4 py-3", m === "wrong" && "bg-destructive/5",
                    active === key && "bg-primary/5")}>
                  {g ? (
                    <span className={cn("inline-flex w-10 shrink-0 justify-center rounded px-1 py-0.5 font-medium tabular-nums", BAND_BADGE[band(g.score)])}>
                      {g.score}
                    </span>
                  ) : failed ? (
                    <span data-testid="score-unavailable" aria-hidden className="inline-flex h-6 w-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">—</span>
                  ) : (
                    <span data-testid="score-pending" aria-hidden className="h-6 w-10 shrink-0 rounded bg-muted motion-safe:animate-pulse" />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span>{x.paper.title}</span>
                    <span className="line-clamp-2 text-xs text-muted-foreground" title={g?.justification || undefined}>
                      {x.paper.year ?? "n.d."} · {[...x.directions].map((d) => DIRECTION_LABEL[d]).join(", ")}
                      {g?.justification ? <> · <span>{g.justification}</span></> : null}
                    </span>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {(["right", "wrong"] as const).map((kind) => (
                      <Button key={kind} size="icon-sm" variant="outline"
                        aria-label={`${kind === "right" ? "Looks right" : "Looks wrong"}: ${shortTitle(x.paper.title)}`}
                        aria-pressed={m === kind}
                        disabled={!g || marksLocked}
                        onClick={() => g && toggle(key, kind, g.score)}
                        className={cn("text-muted-foreground", m === kind && MARK_ON[kind])}>
                        {kind === "right" ? <Check aria-hidden /> : <X aria-hidden />}
                      </Button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {showOffTopic && <OffTopicBox off={offCount} total={offTotal} basis={laneOff ? "lanes" : "sample"} blame={blame} onRefind={() => refind("box")} />}
        {failed && <p role="alert">{failed}</p>}
        {regradeError && <p role="alert" className="text-destructive">{regradeError}</p>}

        {boxOpen ? (
          <SharpenBox
            key={boxOpen === "marks" ? `marks-${marksKey}` : "edit"}
            question={question}
            wrong={boxOpen === "marks" ? wrong : []}
            sharpening={sharpen.isPending}
            busy={update.isPending || grading}
            onSharpen={() => sharpen.mutateAsync({ question, marks: allMarks })}
            onApply={apply}
            onKeep={keep}
          />
        ) : (
          <Button variant="link" className="w-fit px-0" onClick={() => setBoxOpen("edit")}>Edit the question yourself</Button>
        )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {!showOffTopic && (
          <Button variant="link" className="px-0 text-primary" onClick={() => refind("link")}>These seeds look wrong</Button>
        )}
        <span className="grow" />
        {toGrade > 0 && (
          <span className="text-sm text-muted-foreground">Grades ~{toGrade} papers in the background · usually under a minute to start</span>
        )}
        {/* Not while the question saves: ④ would build against the old one. */}
        <Button size="lg" onClick={onStart} disabled={update.isPending}>
          {failed ? "Start anyway" : "Start →"}
        </Button>
      </div>
    </section>
  );
}
